/**
 * Kebab de ações da linha de Casos (E13.F5.T02).
 *
 * Item único: "Abrir" (sempre ativo, dispara onOpen). E27.F1 removeu o
 * placeholder "Arquivar (Em breve)" — volta junto com a rota de archive.
 * Popover manual via `<details>` para não introduzir dependência.
 *
 * E72.F2: "Baixar relatório preliminar" só aparece no caso encerrado COM
 * relatório. Oferecê-lo sempre trocaria um menu honesto por um que promete
 * documento a quem encerrou sem documento — e o servidor responderia 422 depois
 * do clique, que é o pior lugar para a pessoa descobrir.
 *
 * E201.F1 (GUS-47, ADR-0144): no caso eletrônico, "Análise preliminar" sai do menu. A pré-análise é
 * parte da mesa única, a que "Abrir" já leva, e o item repetiria a mesma ação.
 */
import { useEffect, useId, useRef } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';

interface CasosRowActionsProps {
  caseRecord: Pick<
    CaseRecordData,
    'id' | 'display_code' | 'prioritario' | 'workflow_status' | 'modalidade'
  >;
  onOpen: () => void;
  onPreAnalise: () => void;
  onTogglePrioridade: () => void;
  onBaixarRelatorio: () => void;
  alterandoPrioridade?: boolean;
  baixandoRelatorio?: boolean;
}

export function CasosRowActions({
  caseRecord,
  onOpen,
  onPreAnalise,
  onTogglePrioridade,
  onBaixarRelatorio,
  alterandoPrioridade = false,
  baixandoRelatorio = false,
}: CasosRowActionsProps): JSX.Element {
  const temRelatorio = caseRecord.workflow_status === 'encerrado_com_relatorio';
  const ref = useRef<HTMLDetailsElement>(null);
  const menuId = useId();

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.open) return;
      if (ref.current.contains(event.target as Node)) return;
      ref.current.open = false;
    }
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  function close() {
    if (ref.current) ref.current.open = false;
  }

  return (
    <details
      ref={ref}
      className="relative inline-block"
      data-testid={`casos-row-actions-${caseRecord.display_code}`}
    >
      <summary
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label={ptBR.casos.table.rowActionsAria.replace(
          '{{display_code}}',
          caseRecord.display_code,
        )}
        className="cursor-pointer list-none border border-transparent px-2 py-1 text-fg-muted transition-colors duration-fast ease-sc hover:border-border hover:bg-fg/[0.07]"
      >
        ⋮
      </summary>
      <ul
        id={menuId}
        role="menu"
        className="absolute right-0 top-full z-10 mt-1 min-w-[160px] border border-border bg-surface py-1 text-body-sm shadow-md"
      >
        <li role="none">
          <button
            type="button"
            role="menuitem"
            data-testid={`casos-row-action-open-${caseRecord.display_code}`}
            onClick={() => {
              close();
              onOpen();
            }}
            className="block w-full px-3 py-1.5 text-left text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.05]"
          >
            {ptBR.casos.table.actionOpen}
          </button>
        </li>
        {caseRecord.modalidade !== 'eletronica' ? (
          <li role="none">
            <button
              type="button"
              role="menuitem"
              data-testid={`casos-row-action-pre-analise-${caseRecord.display_code}`}
              onClick={() => {
                close();
                onPreAnalise();
              }}
              className="block w-full px-3 py-1.5 text-left text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.05]"
            >
              {ptBR.casos.table.actionPreAnalise}
            </button>
          </li>
        ) : null}
        {temRelatorio ? (
          <li role="none">
            <button
              type="button"
              role="menuitem"
              disabled={baixandoRelatorio}
              data-testid={`casos-row-action-relatorio-${caseRecord.display_code}`}
              onClick={() => {
                close();
                onBaixarRelatorio();
              }}
              className="block w-full px-3 py-1.5 text-left text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.05] disabled:opacity-60"
            >
              {baixandoRelatorio
                ? ptBR.casos.table.actionBaixandoRelatorio
                : ptBR.casos.table.actionBaixarRelatorio}
            </button>
          </li>
        ) : null}
        <li role="none">
          {/* E60.F3: o rótulo diz o que o clique FAZ, não o estado atual — um
              item chamado "Prioritário" num caso já prioritário deixa ambíguo se
              marca ou desmarca. */}
          <button
            type="button"
            role="menuitem"
            disabled={alterandoPrioridade}
            data-testid={`casos-row-action-prioridade-${caseRecord.display_code}`}
            onClick={() => {
              close();
              onTogglePrioridade();
            }}
            className="block w-full px-3 py-1.5 text-left text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.05] disabled:opacity-60"
          >
            {caseRecord.prioritario === true
              ? ptBR.casos.table.actionRemoverPrioridade
              : ptBR.casos.table.actionPriorizar}
          </button>
        </li>
      </ul>
    </details>
  );
}
